import {
  BiBlendSpec,
  BiCardQuery,
  BiTotalsSpec,
  biBlendKeyColumn,
  biBlendMeasureKey,
  isBiBlendSpec,
  isBiTotalsSpec,
} from '@euler/model/bi/blend';
import { BiCatalogField, BiDatasetCatalog } from '@euler/model/bi/catalog';
import {
  BiDatasetRefKind,
  BiMeasureKind,
  BiQuerySpec,
  biDimensionKey,
  biMeasureKey,
} from '@euler/model/bi/query';
import {
  BiFieldRole,
  BiFieldType,
  BiQueryResult,
  BiResultColumn,
} from '@euler/model/bi/result';
import { kBiBuiltinDatasetsFixture } from './biBuiltinDatasetsFixture';
import {
  kBiBlendDraftCaptured,
  kBiBlendResultCaptured,
  kBiUploadedDetailsCaptured,
} from './biUploadedCaptured';

/** The catalogs a real built-in section returns (the editor's datasets). */
export const kBiCatalogFixtures: BiDatasetCatalog[] =
  kBiBuiltinDatasetsFixture.datasets.flatMap(entry =>
    entry.kind === BiDatasetRefKind.Sql ? [entry.catalog] : [],
  );

const kSamples: Record<string, string[]> = {
  service_type: ['常规保养', '钣金喷漆', '轮胎更换', '美容洗护'],
  name: ['机油更换', '四轮定位', '刹车片更换', '空调滤芯', '蓄电池'],
  vehicle_brand: ['丰田', '本田', '大众', '比亚迪', '特斯拉'],
  service_agent: ['张顾问', '刘顾问', '陈顾问'],
};

/** A made-up but stable result for a plain spec. */
function plainQuery(spec: BiQuerySpec): BiQueryResult {
  const catalog = [
    ...kBiCatalogFixtures,
    ...kBiUploadedDetailsCaptured.map(detail => detail.catalog),
  ].find(entry =>
    entry.dataset.kind === BiDatasetRefKind.Sql
      ? spec.dataset.kind === BiDatasetRefKind.Sql &&
        entry.dataset.model === spec.dataset.model
      : spec.dataset.kind === BiDatasetRefKind.Upload &&
        entry.dataset.datasetId === spec.dataset.datasetId,
  );
  // A field the catalog does not list (a ratio, a stale key) reads as a
  // plain number, as the editor keeps such parts.
  const fieldOf = (key: string): BiCatalogField =>
    catalog?.fields.find(field => field.key === key) ?? {
      key,
      label: { zh: key, en: key },
      role: BiFieldRole.Measure,
      type: BiFieldType.Number,
    };
  const columns: BiResultColumn[] = [
    ...spec.dimensions.map(ref => ({
      key: biDimensionKey(ref),
      label: fieldOf(ref.field).label,
      type: fieldOf(ref.field).type,
      role: BiFieldRole.Dimension,
    })),
    ...spec.measures.map(ref => {
      const key = biMeasureKey(ref);
      const field = fieldOf(
        ref.kind === BiMeasureKind.Field ? ref.field : ref.key,
      );
      return {
        key,
        label: field.label,
        type: BiFieldType.Number,
        role: BiFieldRole.Measure,
        ...(field.format ? { format: field.format } : {}),
      };
    }),
  ];
  const first = spec.dimensions[0];
  const values = !first
    ? ['']
    : fieldOf(first.field).grains
    ? Array.from(
        { length: 8 },
        (_, i) =>
          `2026-0${Math.floor(i / 3) + 7}-${String((i % 3) * 9 + 1).padStart(
            2,
            '0',
          )}`,
      )
    : kSamples[first.field] ?? ['A', 'B', 'C'];
  const second = spec.dimensions[1];
  const splits = second
    ? (kSamples[second.field] ?? ['A', 'B']).slice(0, 3)
    : [null];
  const rows = values.flatMap((value, i) =>
    splits.map((split, j) => [
      ...(first ? [value] : []),
      ...(second ? [split] : []),
      ...spec.measures.map(
        (_, k) => ((i * 37 + j * 53 + k * 11) % 17) * 13000 + 42000,
      ),
    ]),
  );
  return {
    columns,
    rows: rows.slice(0, spec.limit ?? 100),
    truncated: false,
    asOf: new Date().toISOString(),
  };
}

/** JSON with keys sorted, so field order does not matter. */
function stable(value: unknown): string {
  return JSON.stringify(value, (_, entry) =>
    entry && typeof entry === 'object' && !Array.isArray(entry)
      ? Object.fromEntries(
          Object.entries(entry).sort(([a], [b]) => (a < b ? -1 : 1)),
        )
      : entry,
  );
}

function catalogLabel(spec: BiQuerySpec): string {
  return (
    [
      ...kBiCatalogFixtures,
      ...kBiUploadedDetailsCaptured.map(detail => detail.catalog),
    ].find(
      entry => JSON.stringify(entry.dataset) === JSON.stringify(spec.dataset),
    )?.label.zh ?? ''
  );
}

/**
 * A blend shaped as the server's: the captured orders × targets one as it
 * came back, any other made up from each side's fixture rows.
 */
function blendQuery(spec: BiBlendSpec): BiQueryResult {
  if (stable(spec) === stable(kBiBlendDraftCaptured.spec))
    return kBiBlendResultCaptured;
  const sides = spec.queries.map(plainQuery);
  const keys = spec.on.length;
  const columns: BiResultColumn[] = [
    ...spec.on.map((on, i) => ({
      ...sides[0].columns[i],
      key: biBlendKeyColumn(on),
    })),
    ...spec.queries.flatMap((side, index) =>
      side.measures.map((ref, k) => ({
        ...sides[index].columns[keys + k],
        key: biBlendMeasureKey(index, ref),
        label: {
          zh: `${sides[index].columns[keys + k].label.zh}（${catalogLabel(
            side,
          )}）`,
          en: sides[index].columns[keys + k].label.en,
        },
        side: index,
      })),
    ),
  ];
  return {
    columns,
    rows: sides[0].rows.map((row, i) => [
      ...row,
      ...(sides[1].rows[i]?.slice(keys) ??
        spec.queries[1].measures.map(() => null)),
    ]),
    truncated: false,
    asOf: new Date().toISOString(),
  };
}

/** Totals as the server reads them: each part's one row, side by side. */
function totalsQuery(spec: BiTotalsSpec): BiQueryResult {
  const parts = spec.queries.map(plainQuery);
  return {
    columns: spec.queries.flatMap((part, index) =>
      part.measures.map((ref, k) => ({
        ...parts[index].columns[k],
        key: biBlendMeasureKey(index, ref),
        side: index,
      })),
    ),
    rows: [parts.flatMap(part => part.rows[0] ?? [])],
    truncated: false,
    asOf: new Date().toISOString(),
  };
}

/** A made-up but stable result for a card query, shaped as the server's. */
export function biFixtureQuery(spec: BiCardQuery): BiQueryResult {
  if (isBiTotalsSpec(spec)) return totalsQuery(spec);
  return isBiBlendSpec(spec) ? blendQuery(spec) : plainQuery(spec);
}
